import { Flame, ArrowRight, Check, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { TIER_META, MODES } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate(user.onboarded ? "/app/submit" : "/onboarding", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const geo = await api.get("/geo");
        const p = await api.get("/pricing", { params: { region: geo.data.detected_region } });
        setPricing(p.data);
      } catch (e) {
        try { setPricing((await api.get("/pricing")).data); } catch (_) {}
      }
    })();
  }, []);

  const tiers = pricing
    ? Object.keys(pricing.tiers).map((k) => ({
        name: TIER_META[k].label, price: `${pricing.symbol}${pricing.tiers[k]}`,
        color: TIER_META[k].color, desc: TIER_META[k].desc,
      }))
    : [];

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame className="text-brand" size={26} />
          <span className="font-heading font-bold text-xl tracking-tighter">RoastLit</span>
        </div>
        <button data-testid="header-login-btn" onClick={login} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 hover:bg-white/10 border border-white/10 transition-colors">
          Sign in
        </button>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand/30 bg-brand/10 text-brand text-xs font-bold tracking-widest uppercase mb-6">
            <Flame size={13} /> Get roasted by real humans
          </div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-5xl sm:text-7xl leading-[0.95] text-shadow-fire">
            Pay to get <span className="text-brand">roasted</span>. Or roast for cash.
          </h1>
          <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
            Submit your photo, bio, resume line or pitch. Real roasters serve you a custom burn.
            Pick the best one — they get paid a flat rate. No prizes, no gambling, just heat.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="hero-login-btn" onClick={login} className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">
              Continue with Google <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <a href="/hot-takes" data-testid="landing-hottakes-btn"
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-brand/50 text-brand hover:bg-brand/10 font-bold transition-colors">
              <Zap size={18} className="fill-brand" /> Try Hot Takes — free, no account
            </a>
          </div>
          <div className="inline-flex items-center gap-2 text-sm text-zinc-400 mt-4">
            <Check size={16} className="text-tier-mild" /> 18+ only · real birthdate required · content guidelines apply
          </div>
        </div>

        {/* Visual proof — a designed mock survivor certificate (not a live feed pull) */}
        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-center">
          <div data-testid="landing-proof" className="mx-auto w-full max-w-sm animate-fade-up">
            <div className="relative rounded-lg overflow-hidden border border-[#d4af37]/40 bg-[#0a0a0c] p-8 text-center" style={{ fontFamily: "Georgia, serif", boxShadow: "0 0 60px rgba(212,175,55,0.12)" }}>
              <div className="absolute inset-3 border-2 border-[#d4af37] rounded-md pointer-events-none" />
              <div className="absolute inset-4 border border-[#d4af37]/50 rounded pointer-events-none" />
              <div className="relative">
                <div className="text-[11px] tracking-[5px] uppercase text-[#d4af37]">Certificate of</div>
                <div className="font-heading font-bold text-2xl uppercase tracking-wide mt-1">Endurance</div>
                <div className="mx-auto my-5 w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 35% 30%, #d4af37, #8a6d1f)" }}>
                  <span className="font-heading font-bold text-3xl text-[#1a1508]">S</span>
                </div>
                <div className="font-heading font-bold text-2xl uppercase" style={{ color: TIER_META.savage.color }}>Certified Savage Survivor</div>
                <div className="text-sm text-zinc-400 italic mt-3">"Submitted to be roasted by real humans — and lived to post it."</div>
                <div className="w-16 h-px bg-[#d4af37] mx-auto my-4" />
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Certified by</div>
                <div className="font-heading font-bold text-lg">@theburnunit</div>
                <div className="text-[11px] text-[#d4af37] uppercase tracking-widest">Legend-tier roaster</div>
              </div>
            </div>
            <p className="text-center text-xs text-zinc-500 mt-3">Survive a roast → earn a shareable certificate & Survival Score.</p>
          </div>

          <div>
            <h2 className="font-heading font-bold uppercase tracking-tighter text-3xl mb-2">Five ways to play</h2>
            <p className="text-zinc-400 mb-6 text-sm">Two paid, three free. Start anywhere.</p>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="landing-modes">
              {MODES.map((m, i) => (
                <button key={m.key} onClick={login} data-testid={`landing-${m.tid}`}
                  className="text-left animate-fade-up rounded-sm border border-white/10 bg-zinc-900/60 p-5 hover:-translate-y-0.5 transition-all"
                  style={{ animationDelay: `${i * 70}ms`, borderLeft: `3px solid ${m.accent}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = m.accent)}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderLeftColor = m.accent; }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-heading font-bold text-lg">{m.emoji} {m.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${m.accent}22`, color: m.accent }}>{m.tag}</span>
                  </div>
                  <div className="text-sm text-zinc-400">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
          {tiers.map((t, i) => (
            <div key={t.name} className="animate-fade-up rounded-sm border border-white/10 bg-zinc-900/60 p-6 hover:-translate-y-0.5 transition-transform"
              style={{ animationDelay: `${i * 80}ms`, boxShadow: i >= 2 ? `0 0 24px ${t.color}22` : "none" }}>
              <div className="text-xs font-bold tracking-[0.2em] uppercase mb-2" style={{ color: t.color }}>{t.name}</div>
              <div className="font-heading text-4xl font-bold">{t.price}</div>
              <div className="text-sm text-zinc-500 mt-1">{t.desc}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
